import { useAuth0 } from '@auth0/auth0-react';
import { Search } from '@mui/icons-material';
import { SxProps } from '@mui/material';
import { useCallback, useContext } from 'react';

import { getUsersContaining } from '@/apis/users';
import { TemplatePlaceholder } from '@/types';
import TemplateCapableFetchAutocomplete, {
  TemplateCapableFetchAutocompleteChangeReason,
} from '@/components/TemplateCapableFetchAutocomplete';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import { labelSorting } from '@/util/labelSorting';
import { AutocompleteGenericEntity } from '@australiangreens/ag-internal-components';

type UsersFetchAutocompleteProps = {
  onChange: (
    newValue: AutocompleteGenericEntity[] | TemplatePlaceholder,
    reason: TemplateCapableFetchAutocompleteChangeReason
  ) => void;
  value: AutocompleteGenericEntity[] | TemplatePlaceholder;
  minLength?: number;
  label?: string;
  dataTestidPrefix?: string;
  loadingText?: string;
  noOptionsText?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: SxProps;
};

export const useLookupUsers = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useCallback(
    async (lookupValue: string) => {
      const token = await getAccessTokenSilently();
      return (await getUsersContaining(token, lookupValue))
        .map((x) => {
          return {
            id: x.id,
            label: x.name,
          };
        })
        .sort(labelSorting);
    },
    [getAccessTokenSilently]
  );
};

const UsersFetchAutocomplete = ({
  value = [],
  onChange,
  minLength = 3,
  label = 'Search for users',
  dataTestidPrefix = 'getfilterusers',
  loadingText = 'Looking for users...',
  noOptionsText = 'No users found',
  error = false,
  helperText = '',
  sx,
}: UsersFetchAutocompleteProps) => {
  const lookupUsers = useLookupUsers();
  const isOnTemplatePage = useContext(SearchTemplatePageContext);

  return (
    <TemplateCapableFetchAutocomplete
      lookup={lookupUsers}
      minLength={minLength}
      label={label}
      value={value}
      onChange={onChange}
      data-testid={dataTestidPrefix}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      popupIcon={<Search />}
      disableIconFlip
      error={error}
      helperText={helperText}
      sx={sx}
      isOnTemplatePage={isOnTemplatePage}
      templateBuilderDisplayName="Calling status filter: User(s)"
    />
  );
};

export default UsersFetchAutocomplete;
